package co.maytes.standupbot.dashboard.features.analytics

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.components.widgets.Popover
import co.maytes.standupbot.dashboard.components.widgets.PopoverPlacement
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

private fun orderedCustom(from: LocalDate, to: LocalDate): DateRange.Custom =
    if (from > to) DateRange.Custom(to, from) else DateRange.Custom(from, to)

@Composable
fun DateRangePicker(
    value: DateRange,
    onChange: (DateRange) -> Unit,
) {
    var customOpen by remember { mutableStateOf(value.preset == DateRange.Preset.CUSTOM) }
    var customFrom by remember { mutableStateOf(value.from) }
    var customTo by remember { mutableStateOf(value.to) }

    Column(modifier = Modifier.position(Position.Relative).gap(Spacing.xs)) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .padding(3.px)
                .gap(2.px)
                .borderRadius(Radius.full)
                .backgroundColor(Palette.surfaceSubtle)
                .styleModifier {
                    property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                    property("box-shadow", "inset 0 1px 2px rgba(0, 0, 0, 0.03)")
                },
        ) {
            PresetPill(
                label = "7d",
                selected = value.preset == DateRange.Preset.D7,
                onClick = {
                    customOpen = false
                    onChange(DateRange.last7())
                },
            )
            PresetPill(
                label = "30d",
                selected = value.preset == DateRange.Preset.D30,
                onClick = {
                    customOpen = false
                    onChange(DateRange.last30())
                },
            )
            PresetPill(
                label = "90d",
                selected = value.preset == DateRange.Preset.D90,
                onClick = {
                    customOpen = false
                    onChange(DateRange.last90())
                },
            )
            PresetPill(
                label = "Week",
                selected = value.preset == DateRange.Preset.THIS_WEEK,
                onClick = { customOpen = false; onChange(DateRange.thisWeek()) },
            )
            PresetPill(
                label = "2w",
                selected = value.preset == DateRange.Preset.LAST_2_WEEKS,
                onClick = { customOpen = false; onChange(DateRange.last2Weeks()) },
            )
            PresetPill(
                label = "Month",
                selected = value.preset == DateRange.Preset.THIS_MONTH,
                onClick = { customOpen = false; onChange(DateRange.thisMonth()) },
            )
            PresetPill(
                label = "Custom",
                selected = value.preset == DateRange.Preset.CUSTOM,
                onClick = {
                    customOpen = !customOpen
                    if (!customOpen && value.preset == DateRange.Preset.CUSTOM) {
                        onChange(DateRange.last30())
                    } else {
                        onChange(DateRange.Custom(customFrom, customTo))
                    }
                },
            )
        }

        CustomRangePopover(
            open = customOpen,
            from = customFrom,
            to = customTo,
            onDismiss = { customOpen = false },
            onFromChange = {
                customFrom = it
                onChange(orderedCustom(it, customTo))
            },
            onToChange = {
                customTo = it
                onChange(orderedCustom(customFrom, it))
            },
        )
    }
}

@Composable
private fun PresetPill(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
) {
    var hovered by remember { mutableStateOf(false) }
    val textColor = when {
        selected -> Palette.accent
        hovered -> Palette.textPrimary
        else -> Palette.textSecondary
    }
    Box(
        modifier = Modifier
            .padding(topBottom = Spacing.xs, leftRight = Spacing.md)
            .borderRadius(Radius.full)
            .cursor(Cursor.Pointer)
            .backgroundColor(
                when {
                    selected -> Palette.surface
                    hovered -> Palette.surfaceHover
                    else -> Palette.surfaceSubtle
                },
            )
            .color(textColor)
            .fontSize(Typography.bodySmall.size)
            .fontWeight(if (selected) 600 else 500)
            .attrsModifier {
                onMouseEnter { hovered = true }
                onMouseLeave { hovered = false }
            }
            .pressable()
            .styleModifier {
                property("user-select", "none")
                property("white-space", "nowrap")
                if (selected) {
                    property("box-shadow", "${Shadow.SM}, 0 0 0 1px ${Palette.accentBorder.cssRgb()}")
                }
            }
            .onClick { onClick() },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(label)
    }
}

@Composable
private fun CustomRangePopover(
    open: Boolean,
    from: LocalDate,
    to: LocalDate,
    onDismiss: () -> Unit,
    onFromChange: (LocalDate) -> Unit,
    onToChange: (LocalDate) -> Unit,
) {
    Popover(
        open = open,
        placement = PopoverPlacement.BottomStart,
        onDismiss = onDismiss,
        role = "group",
        ariaLabel = "Custom date range",
        modifier = Modifier
            .backgroundColor(Palette.surface)
            .borderRadius(Radius.lg)
            .styleModifier {
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("box-shadow", Shadow.LG)
            },
    ) {
        Row(
            verticalAlignment = Alignment.Bottom,
            modifier = Modifier
                .gap(Spacing.sm)
                .padding(Spacing.md),
        ) {
            DateField("From", from, onFromChange)
            SpanText(
                text = "→",
                modifier = Modifier
                    .color(Palette.textTertiary)
                    .fontSize(Typography.bodySmall.size)
                    .margin(bottom = Spacing.xs),
            )
            DateField("To", to, onToChange)
        }
    }
}

@Composable
private fun DateField(
    label: String,
    value: LocalDate,
    onChange: (LocalDate) -> Unit,
) {
    co.maytes.standupbot.dashboard.components.widgets.DatePicker(
        value = value.toString(),
        onChange = { raw ->
            if (raw.isBlank()) return@DatePicker
            runCatching { LocalDate.parse(raw) }.getOrNull()?.let(onChange)
        },
        label = label,
        widthPx = 150,
    )
}

